"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback } from "react";

type Params = Record<string, string | null>;

export function useQuery() {
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();

	// Get a new searchParams string by merging the current
	// searchParams with a provided key/value pair
	const createQueryString = useCallback(
		(entries: Params) => {
			const params = new URLSearchParams(Object.fromEntries(searchParams.entries()));

			for (const [name, value] of Object.entries(entries)) {
				if (value) {
					params.set(name, value);
				} else {
					params.delete(name);
				}
			}

			return params.toString();
		},
		[searchParams],
	);

	const createUrl = useCallback(
		(entries: Params, href?: string) => {
			const params = createQueryString(entries);
			const url = href ?? pathname;

			return url + (params ? `?${params}` : "");
		},
		[createQueryString, pathname],
	);

	return {
		router,
		pathname,
		searchParams,
		createQueryString,
		createUrl,
	} as const;
}
